import { useEffect, useMemo, useState } from "react";
import EmojiPicker, { Theme, type EmojiClickData } from "emoji-picker-react";
import { useChatTheme } from "@/hooks/useChatTheme";
import { searchChatGifs } from "@/lib/chatApi";

export type ChatGifItem = {
  id: string;
  preview: string;
  url: string;
  title: string;
};

/** Réactions GIF sans clé API (CDN publics). */
const CURATED_GIFS: ChatGifItem[] = [
  {
    id: "thumbs",
    title: "OK",
    preview: "https://media.giphy.com/media/111ebonMs90YLu/giphy.gif",
    url: "https://media.giphy.com/media/111ebonMs90YLu/giphy.gif",
  },
  {
    id: "clap",
    title: "Bravo",
    preview: "https://media.giphy.com/media/7rj2ZgttXXmhBfiRLY/giphy.gif",
    url: "https://media.giphy.com/media/7rj2ZgttXXmhBfiRLY/giphy.gif",
  },
  {
    id: "party",
    title: "Party",
    preview: "https://media.giphy.com/media/l0MYt5jPR6QX5pnqM/giphy.gif",
    url: "https://media.giphy.com/media/l0MYt5jPR6QX5pnqM/giphy.gif",
  },
  {
    id: "love",
    title: "Love",
    preview: "https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif",
    url: "https://media.giphy.com/media/3o7abKhOpu0NwenH3O/giphy.gif",
  },
  {
    id: "lol",
    title: "MDR",
    preview: "https://media.giphy.com/media/10JhviFuU2gWD6/giphy.gif",
    url: "https://media.giphy.com/media/10JhviFuU2gWD6/giphy.gif",
  },
  {
    id: "wow",
    title: "Wow",
    preview: "https://media.giphy.com/media/5VKbvrjxpVJCM/giphy.gif",
    url: "https://media.giphy.com/media/5VKbvrjxpVJCM/giphy.gif",
  },
  {
    id: "cry",
    title: "Sad",
    preview: "https://media.giphy.com/media/ROF8OQvDmxhdm/giphy.gif",
    url: "https://media.giphy.com/media/ROF8OQvDmxhdm/giphy.gif",
  },
  {
    id: "fire",
    title: "Fire",
    preview: "https://media.giphy.com/media/xT0xeJpnWWcPLrWPi0/giphy.gif",
    url: "https://media.giphy.com/media/xT0xeJpnWWcPLrWPi0/giphy.gif",
  },
];

const PICKER_BODY_H = 340;

type Tab = "emoji" | "gif";

type Props = {
  suiteUserId: string;
  onEmoji: (emoji: string) => void;
  onGif: (gif: ChatGifItem) => void;
  onClose?: () => void;
};

export function ChatEmojiGifPicker({ suiteUserId, onEmoji, onGif, onClose }: Props) {
  const { theme } = useChatTheme();
  const [tab, setTab] = useState<Tab>("emoji");
  const [query, setQuery] = useState("");
  const [remote, setRemote] = useState<ChatGifItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [gifReady, setGifReady] = useState<boolean | null>(null);

  useEffect(() => {
    if (tab !== "gif") return;
    let cancelled = false;
    setLoading(true);
    const t = window.setTimeout(() => {
      void searchChatGifs(suiteUserId, query).then((r) => {
        if (cancelled) return;
        setLoading(false);
        if (!r.success || !r.data) {
          setGifReady(false);
          setRemote([]);
          return;
        }
        setGifReady(r.data.configured);
        setRemote(r.data.gifs);
      });
    }, query.trim() ? 350 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query, tab, suiteUserId]);

  const gifs = useMemo(() => {
    if (remote.length > 0) return remote;
    if (!query.trim()) return CURATED_GIFS;
    if (gifReady) return [];
    const q = query.trim().toLowerCase();
    return CURATED_GIFS.filter(
      (g) => g.title.toLowerCase().includes(q) || g.id.includes(q)
    );
  }, [query, remote, gifReady]);

  return (
    <div className="chat-emoji-gif-picker w-[min(100vw-1.5rem,22rem)] overflow-hidden rounded-xl border border-[var(--chat-border)] bg-[var(--chat-surface)] shadow-[var(--chat-shadow)]">
      <div className="relative z-[1] flex items-center gap-1 border-b border-[var(--chat-border)] bg-[var(--chat-surface)] p-1.5">
        <button
          type="button"
          className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold ${
            tab === "emoji"
              ? "bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]"
              : "text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)]"
          }`}
          onClick={() => setTab("emoji")}
        >
          Émojis
        </button>
        <button
          type="button"
          className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold ${
            tab === "gif"
              ? "bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]"
              : "text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)]"
          }`}
          onClick={() => setTab("gif")}
        >
          GIF
        </button>
        {onClose ? (
          <button
            type="button"
            className="rounded-lg px-2 py-1.5 text-xs text-[var(--chat-muted)]"
            onClick={onClose}
          >
            Fermer
          </button>
        ) : null}
      </div>

      {/* Hauteur fixe : l’onglet GIF ne laisse plus voir la grille emoji derrière */}
      <div
        className="chat-emoji-gif-picker__body relative isolate bg-[var(--chat-surface)]"
        style={{ height: PICKER_BODY_H }}
      >
        <div
          className={`absolute inset-0 overflow-hidden bg-[var(--chat-surface)] ${
            tab === "emoji" ? "z-[2]" : "pointer-events-none z-0 invisible"
          }`}
          aria-hidden={tab !== "emoji"}
        >
          <EmojiPicker
            theme={theme === "dark" ? Theme.DARK : Theme.LIGHT}
            width="100%"
            height={PICKER_BODY_H}
            searchPlaceHolder="Rechercher…"
            previewConfig={{ showPreview: false }}
            onEmojiClick={(data: EmojiClickData) => onEmoji(data.emoji)}
            lazyLoadEmojis
          />
        </div>
        <div
          className={`absolute inset-0 flex flex-col gap-2 overflow-hidden bg-[var(--chat-surface)] p-2 ${
            tab === "gif" ? "z-[2]" : "pointer-events-none z-0 invisible"
          }`}
          aria-hidden={tab !== "gif"}
        >
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              gifReady === false ? "Filtrer les réactions…" : "Rechercher un GIF…"
            }
            className="w-full shrink-0 rounded-lg border border-[var(--chat-border)] bg-[var(--chat-bg)] px-2.5 py-1.5 text-sm outline-none focus:border-[var(--chat-accent)]"
            tabIndex={tab === "gif" ? 0 : -1}
          />
          {gifReady === false ? (
            <p className="shrink-0 text-[10px] leading-snug text-[var(--chat-muted)]">
              Catalogue local uniquement. La recherche demande une clé Giphy (gratuite)
              côté serveur — variable{" "}
              <code className="text-[var(--chat-accent-strong)]">GIPHY_API_KEY</code> ou{" "}
              <code className="text-[var(--chat-accent-strong)]">local-config.php</code>.
            </p>
          ) : null}
          {loading ? (
            <p className="py-6 text-center text-xs text-[var(--chat-muted)]">Recherche…</p>
          ) : gifs.length === 0 ? (
            <p className="py-6 text-center text-xs text-[var(--chat-muted)]">Aucun GIF.</p>
          ) : (
            <div className="min-h-0 flex-1 columns-2 gap-1.5 overflow-y-auto">
              {gifs.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="mb-1.5 block w-full break-inside-avoid overflow-hidden rounded-lg bg-[var(--chat-bg)]"
                  title={g.title}
                  tabIndex={tab === "gif" ? 0 : -1}
                  onClick={() => onGif(g)}
                >
                  <img
                    src={g.preview}
                    alt={g.title}
                    className="h-auto w-full object-contain"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** URL média seule dans le corps (GIF envoyé comme message). */
export function extractLoneMediaUrl(body: string): string | null {
  const t = body.trim();
  if (!t || /\s/.test(t)) return null;
  if (/^https?:\/\//i.test(t) && /\.(gif|png|jpe?g|webp)(\?|$)/i.test(t)) return t;
  if (/giphy\.com|tenor\.com|media\.tenor/i.test(t)) return t;
  return null;
}
